import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Tag,
  Space,
  Dropdown,
  message,
  Statistic,
  Avatar,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  TeamOutlined,
  MailOutlined,
  PhoneOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import useStore from '../store/useStore';
import { vendorsAPI, purchaseOrdersAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;
// const { Text } = Typography;

const Vendors = () => {
  const { vendors, setVendors } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  // View state
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [, setViewingVendor] = useState(null); // used when switching to detail
  const [, setVendorPOs] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vendorsAPI.getAll();
      setVendors(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setVendors]);

  const fetchVendorPOs = async (vendorId) => {
    try {
      const response = await purchaseOrdersAPI.getByVendor(vendorId);
      setVendorPOs(response.data);
    } catch (err) {
      console.error('Error fetching vendor purchase orders:', err);
      setVendorPOs([]);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleAddVendor = () => {
    setEditingVendor(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    form.setFieldsValue(vendor);
    setDrawerVisible(true);
  };

  const handleViewVendor = async (vendor) => {
    setViewingVendor(vendor);
    setSelectedVendor(vendor);
    setCurrentView('detail');
    await fetchVendorPOs(vendor.id);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedVendor(null);
  };

  const handleDeleteVendor = async (vendorId) => {
    try {
      await vendorsAPI.delete(vendorId);
      message.success('Vendor deleted successfully');
      await fetchVendors();
    } catch (err) {
      message.error('Failed to delete vendor');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingVendor(null);
    form.resetFields();
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();

      if (editingVendor) {
        await vendorsAPI.update(editingVendor.id, values);
        message.success('Vendor updated successfully');
      } else {
        await vendorsAPI.create(values);
        message.success('Vendor created successfully');
      }

      await fetchVendors();
      handleDrawerClose();
    } catch (err) {
      message.error('Failed to save vendor');
    } finally {
      setDrawerLoading(false);
    }
  };

  const getVendorStatus = (vendor) => {
    if (!vendor.active) {
      return { color: 'red', text: 'Inactive' };
    }
    if (vendor.rating >= 4) {
      return { color: 'green', text: 'Preferred' };
    }
    if (vendor.rating >= 3) {
      return { color: 'blue', text: 'Active' };
    }
    return { color: 'orange', text: 'Under Review' };
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    vendor.company?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={record.avatar}
            icon={<UserOutlined />}
            size={40}
          />
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-sm text-gray-500">{record.company}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-sm">
            <MailOutlined className="text-gray-400" />
            <span>{record.email}</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <PhoneOutlined className="text-gray-400" />
            <span>{record.phone || 'N/A'}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag color="blue">{category}</Tag>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating) => (
        <div className="flex items-center gap-1">
          <span className="font-medium">{rating || 'N/A'}</span>
          {rating && <span className="text-yellow-500">★</span>}
        </div>
      ),
    },
    {
      title: 'Total Orders',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      render: (totalOrders) => (
        <div className="flex items-center gap-1">
          <ShoppingOutlined className="text-gray-400" />
          <span>{totalOrders || 0}</span>
        </div>
      ),
    },
    {
      title: 'Total Spent',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      render: (totalSpent) => (
        <div className="font-medium">${totalSpent?.toFixed(2) || '0.00'}</div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const status = getVendorStatus(record);
        return (
          <Tag color={status.color} icon={
            status.text === 'Preferred' ? <CheckCircleOutlined /> : 
            status.text === 'Inactive' ? <CloseCircleOutlined /> : null
          }>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'View Details',
                icon: <TeamOutlined />,
                onClick: () => handleViewVendor(record),
              },
              {
                key: 'edit',
                label: 'Edit Vendor',
                icon: <EditOutlined />,
                onClick: () => handleEditVendor(record),
              },
              {
                key: 'delete',
                label: 'Delete Vendor',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => handleDeleteVendor(record.id),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading vendors..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchVendors}
      />
    );
  }

  const renderVendorDetail = () => {
    if (!selectedVendor) return null;
    const status = getVendorStatus(selectedVendor);
    return (
      <DetailView
        title={selectedVendor.name}
        subtitle={selectedVendor.company || 'Vendor'}
        onBack={handleBackToList}
        onEdit={() => handleEditVendor(selectedVendor)}
        onDelete={() => {
          Modal.confirm({
            title: 'Delete Vendor',
            content: 'Are you sure you want to delete this vendor?',
            onOk: () => handleDeleteVendor(selectedVendor.id),
          });
        }}
        tags={[{ label: status.text, color: status.color }]}
      >
        <Row gutter={24}>
          <Col span={16}>
            <DetailSection title="Vendor Information">
              <Row gutter={16}>
                <Col span={12}>
                  <DetailField label="Vendor Name" value={selectedVendor.name} />
                  <DetailField label="Company" value={selectedVendor.company || 'N/A'} />
                  <DetailField label="Email" value={selectedVendor.email} />
                </Col>
                <Col span={12}>
                  <DetailField label="Phone" value={selectedVendor.phone || 'N/A'} />
                  <DetailField label="Category" value={selectedVendor.category || 'N/A'} />
                  <DetailField label="Rating" value={selectedVendor.rating} />
                </Col>
              </Row>
              {selectedVendor.address && (
                <DetailField label="Address" value={selectedVendor.address} />
              )}
              {selectedVendor.contactPerson && (
                <DetailField label="Contact Person" value={selectedVendor.contactPerson} />
              )}
              {selectedVendor.paymentTerms && (
                <DetailField label="Payment Terms" value={selectedVendor.paymentTerms} />
              )}
              {selectedVendor.notes && (
                <DetailField label="Notes" value={selectedVendor.notes} />
              )}
            </DetailSection>
          </Col>
          <Col span={8}>
            <DetailSection title="Quick Stats">
              <Space direction="vertical" className="w-full">
                <Card>
                  <Statistic title="Total Orders" value={selectedVendor.totalOrders || 0} prefix={<ShoppingOutlined />} />
                </Card>
                <Card>
                  <Statistic title="Total Spent" value={selectedVendor.totalSpent || 0} prefix={<DollarOutlined />} precision={2} />
                </Card>
              </Space>
            </DetailSection>
          </Col>
        </Row>
      </DetailView>
    );
  };

  const renderDrawerForm = () => (
    <RightDrawer
      title={editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
      open={drawerVisible}
      onClose={handleDrawerClose}
      loading={drawerLoading}
      width={640}
    >
      <Form form={form} layout="vertical" className="mt-4" onFinish={handleDrawerSave} id="drawer-form">
        <Row gutter={16}>
          <Col span={12}>
            <FormField
              name="name"
              label="Vendor Name"
              rules={[{ required: true, message: 'Please enter vendor name' }]}
              placeholder="Enter vendor name"
            />
          </Col>
          <Col span={12}>
            <FormField
              name="company"
              label="Company"
              placeholder="Enter company name"
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <FormField
              name="email"
              label="Email"
              type="email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter valid email' }
              ]}
              placeholder="Enter email address"
            />
          </Col>
          <Col span={12}>
            <FormField
              name="phone"
              label="Phone"
              placeholder="Enter phone number"
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <FormField
              name="category"
              label="Category"
              type="select"
              options={[
                { value: 'Electronics', label: 'Electronics' },
                { value: 'Office Supplies', label: 'Office Supplies' },
                { value: 'Manufacturing', label: 'Manufacturing' },
                { value: 'Services', label: 'Services' },
                { value: 'Raw Materials', label: 'Raw Materials' },
              ]}
              placeholder="Select category"
            />
          </Col>
          <Col span={12}>
            <FormField
              name="rating"
              label="Rating"
              type="number"
              min={1}
              max={5}
              placeholder="Enter rating (1-5)"
            />
          </Col>
        </Row>

        <FormField
          name="address"
          label="Address"
          type="textarea"
          placeholder="Enter full address"
          rows={3}
        />

        <Row gutter={16}>
          <Col span={12}>
            <FormField
              name="contactPerson"
              label="Contact Person"
              placeholder="Enter contact person name"
            />
          </Col>
          <Col span={12}>
            <FormField
              name="paymentTerms"
              label="Payment Terms"
              placeholder="e.g., Net 30"
            />
          </Col>
        </Row>

        <FormField
          name="notes"
          label="Notes"
          type="textarea"
          placeholder="Enter any additional notes"
          rows={3}
        />

        <FormField
          name="active"
          label="Status"
          type="switch"
          initialValue={true}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      </Form>
    </RightDrawer>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Vendors</h1>
          <p className="text-gray-600">Manage vendor relationships and suppliers</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddVendor}
          className="w-full sm:w-auto"
        >
          Add Vendor
        </Button>
      </div>

      {/* Stats Cards */}
      {currentView === 'list' && (
      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Vendors"
              value={vendors.length}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Vendors"
              value={vendors.filter(v => v.active).length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Preferred Vendors"
              value={vendors.filter(v => v.rating >= 4).length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Spent"
              value={vendors.reduce((sum, v) => sum + (v.totalSpent || 0), 0)}
              prefix={<DollarOutlined />}
              precision={2}
            />
          </Card>
        </Col>
      </Row>
      )}

      {/* Search and Table */}
      {currentView === 'list' && (
        <Card>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <Search
              placeholder="Search vendors..."
              allowClear
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              className="sm:w-80"
            />
          </div>
          <Table
            columns={columns}
            dataSource={filteredVendors}
            rowKey="id"
            rowSelection={rowSelection}
            onRow={(record) => ({
              onClick: () => handleViewVendor(record),
              className: 'cursor-pointer hover:bg-gray-50'
            })}
            pagination={{
              total: filteredVendors.length,
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} vendors`,
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      )}
      {/* Right Drawer for Add/Edit */}
      {renderDrawerForm()}

      {/* In-page Detail View */}
      {currentView === 'detail' && renderVendorDetail()}
    </div>
  );
};

export default Vendors;
